/**
 * Integration service for client-side operations
 * Provides typed API calls for integration operations using class-based approach
 */

import {
  IntegrationsApiResponse,
  IntegrationAction,
} from "@/components/agents/builder/integrations/types";
import { api } from "../utils/api-client";
import { logger } from '@/lib/logger'

/**
 * API response types matching server-side definitions
 */
export interface IntegrationApiResponse {
  id: string;
  name: string;
  description?: string;
  provider: string;
  appId?: string;
  connectionId?: string;
  config: Record<string, any>;
  workspaceId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  authorizeUrl?: string;
}

/**
 * Integration form data types
 */
export interface IntegrationFormData {
  name: string;
  description?: string;
  provider: string;
  appId?: string;
  connectionId?: string;
  apiKey?: string;
  username?: string;
  password?: string;
  publicKey?: string;
  privateKey?: string;
  clientId?: string;
  clientSecret?: string;
  redirectUri?: string;
  scopes?: string;
  baseUrl?: string;
  config?: Record<string, any>;
}

/**
 * TypeScript interface for individual integration response (existing)
 */
export interface IntegrationResponse {
  id: string;
  name: string;
  description: string;
  logoUrl: string;
  isPublished: boolean;
  availableForAgent: boolean;
  isLlmModel: boolean;
  models?: string[];
  connections: IntegrationConnection[];
}

/**
 * TypeScript interface for integration connection (existing)
 */
export interface IntegrationConnection {
  id: string;
  name: string;
  type: string;
  isConnected: boolean;
  connectionDetails?: IntegrationConnectionDetails;
}

/**
 * TypeScript interface for integration connection details (existing)
 */
export interface IntegrationConnectionDetails {
  resourceId: string;
  connectedAt: Date;
  status: string;
}

/**
 * Transform form data to API request format
 *
 * @param formData - Integration form data
 * @returns Formatted request data for API
 */
function transformFormDataToApiRequest(formData: IntegrationFormData): any {
  return {
    name: formData.name,
    description: formData.description || "",
    provider: formData.provider,
    appId: formData.appId,
    connectionId: formData.connectionId,
    apiKey: formData.apiKey,
    username: formData.username,
    password: formData.password,
    publicKey: formData.publicKey,
    privateKey: formData.privateKey,
    clientId: formData.clientId,
    clientSecret: formData.clientSecret,
    redirectUri: formData.redirectUri,
    scopes: formData.scopes,
    baseUrl: formData.baseUrl,
    config: formData.config,
  };
}

/**
 * Integration service class for client-side operations
 * Provides methods for integration CRUD operations
 */
export class IntegrationService {
  /**
   * Fetches all available integrations
   * @returns Promise resolving to integrations response
   */
  static async fetchIntegrations(): Promise<IntegrationsApiResponse> {
    return api.get("/integrations");
  }

  /**
   * Fetches all available actions for a specific integration
   * @param integrationId - The ID of the integration
   * @returns Promise resolving to array of integration actions
   */
  static async fetchIntegrationActions(
    integrationId: string
  ): Promise<IntegrationAction[]> {
    try {
      const response = await api.get(`/integrations/${integrationId}/actions`);
      if (response.success) {
        return response.data;
      } else {
        logger.error("Failed to fetch integration actions", response.message);
        return [];
      }
    } catch (error) {
      logger.error("Error fetching integration actions", error);
      // Return empty array for graceful degradation
      return [];
    }
  }

  /**
   * Fetches detailed information about a specific action
   * @param integrationId - The ID of the integration
   * @param actionId - The ID of the action
   * @returns Promise resolving to integration action or null
   */
  static async fetchIntegrationAction(
    integrationId: string,
    actionId: string
  ): Promise<IntegrationAction | null> {
    try {
      const response = await api.get(
        `/integrations/${integrationId}/actions/${actionId}`
      );

      if (response.success) {
        return response.data;
      } else {
        logger.error("Failed to fetch integration action", response.message);
        return null;
      }
    } catch (error) {
      logger.error("Error fetching integration action", error);
      return null;
    }
  }

  /**
   * Saves a new integration via API
   *
   * @param formData - Integration form data
   * @returns Promise resolving to success/error result
   */
  async saveIntegration(
    formData: IntegrationFormData
  ): Promise<
    | { success: true; data: IntegrationApiResponse }
    | { success: false; error: string }
  > {
    try {
      logger.debug("Saving integration via API", {
        name: formData.name,
        provider: formData.provider,
      });

      const requestData = transformFormDataToApiRequest(formData);
      const response = await api.post("/integrations", requestData);

      if (!response.success) {
        return {
          success: false,
          error:
            (response as any).error?.message || "Failed to save integration",
        };
      }

      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      logger.error("Save integration API error", error);

      // Handle structured API errors
      if (error.type && error.message) {
        return {
          success: false,
          error: error.message,
        };
      }

      return {
        success: false,
        error:
          error?.message || "Failed to save integration. Please try again.",
      };
    }
  }

  /**
   * Tests an integration connection via API
   *
   * @param formData - Integration form data
   * @returns Promise resolving to success/error result
   */
  async testConnection(
    formData: IntegrationFormData
  ): Promise<{ success: boolean; message: string }> {
    try {
      logger.debug("Testing integration connection via API", {
        name: formData.name,
        provider: formData.provider,
      });

      const requestData = transformFormDataToApiRequest(formData);
      const response = await api.post("/integrations/test", requestData);

      return {
        success: response.success,
        message: response.message,
      };
    } catch (error: any) {
      logger.error("Test connection API error", error);

      // Handle structured API errors
      if (error.type && error.message) {
        return {
          success: false,
          message: error.message,
        };
      }

      return {
        success: false,
        message:
          error?.message || "Failed to test connection. Please try again.",
      };
    }
  }

  /**
   * Gets all available integrations via API
   *
   * @returns Promise resolving to success/error result
   */
  async getIntegrations(): Promise<
    { success: true; data: any[] } | { success: false; error: string }
  > {
    try {
      const response = await api.get("/integrations");

      if (!response.success) {
        return {
          success: false,
          error:
            (response as any).error?.message || "Failed to fetch integrations",
        };
      }

      logger.debug("Successfully fetched integrations", {
        count: response.data.length,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      logger.error("Get integrations API error", error);

      // Handle structured API errors
      if (error.type && error.message) {
        return {
          success: false,
          error: error.message,
        };
      }

      return {
        success: false,
        error:
          error?.message || "Failed to fetch integrations. Please try again.",
      };
    }
  }
}

/**
 * Singleton instance of the integration service
 */
const integrationServiceInstance = new IntegrationService();

/**
 * Convenience instance of the integration service
 */
export const integrationService = {
  fetchIntegrations: IntegrationService.fetchIntegrations,
  fetchIntegrationActions: IntegrationService.fetchIntegrationActions,
  fetchIntegrationAction: IntegrationService.fetchIntegrationAction,
  saveIntegration: (formData: IntegrationFormData) =>
    integrationServiceInstance.saveIntegration(formData),
  testConnection: (formData: IntegrationFormData) =>
    integrationServiceInstance.testConnection(formData),
  getIntegrations: () => integrationServiceInstance.getIntegrations(),
};

/**
 * Export utility functions for backward compatibility
 */
export const saveIntegration = (formData: IntegrationFormData) =>
  integrationServiceInstance.saveIntegration(formData);
export const testConnection = (formData: IntegrationFormData) =>
  integrationServiceInstance.testConnection(formData);

export { transformFormDataToApiRequest };

/**
 * Export default for easier imports
 */
